import type { ProductJourney } from "@shared/analytics/productEvents";
import {
    useProductJourney,
    useProductObservation
} from "@shared/analytics/useProductJourney";
import { useSolanaAddressVerification } from "@shared/hooks/useSolanaAddressVerification";
import { SolanaAddress } from "@shared/utils/address";
import { Button, Text, toastManager } from "@solomon-labs/design/components/ui";
import React, { useState } from "react";

import { useRewardsDestination } from "@/hooks/yaas/useRewardsDestination";

import { LabeledRewardsDestination } from "./LabeledRewardsDestination";

interface InlineDestinationEditorProps {
    analytics?: ProductJourney;
    connectedWallet: string;
    walletAddress: string;
    canSubmit: boolean;
    onClose: () => void;
}

/**
 * Inline editor opened from a position row. Submission updates the
 * account-level rewards destination, not a per-position route. Optional
 * label input + RewardsDestinationField (Tier-1
 * shape + Tier-3 on-chain) + a status / CTA row that places the
 * verification text on the left and a fit-width "Verify & Update"
 * CTA right-aligned next to it. `hideOnChainStatus` defers the
 * status copy to this component so we can keep both signals on the
 * same line; the hook is called locally and renders the same
 * tone-to-color mapping the field itself would have used.
 *
 * Inputs always open empty — the user is updating a destination, not
 * confirming the existing one, so a clean form reads as "enter the
 * new one" rather than "edit this string". AnimatePresence remounts
 * the editor on each open/close so this happens for free.
 */
export function InlineDestinationEditor({
    analytics: entryAnalytics,
    connectedWallet,
    walletAddress,
    canSubmit,
    onClose
}: InlineDestinationEditorProps): React.ReactElement {
    const [label, setLabel] = useState("");
    const [address, setAddress] = useState("");
    const localAnalytics = useProductJourney(
        "rewards_destination",
        "position_row",
        walletAddress
    );
    const analytics = entryAnalytics ?? localAnalytics;
    useProductObservation(analytics, "form_viewed");
    const rewardsDestination = useRewardsDestination();
    const verification = useSolanaAddressVerification(address);
    const trimmedAddress = address.trim();
    const shapeValid = SolanaAddress.isShape(trimmedAddress);
    const canUseDestination =
        shapeValid &&
        (verification.status === "found" || verification.status === "new") &&
        !verification.invalid;
    const submitEnabled =
        canSubmit && canUseDestination && !rewardsDestination.isLoading;
    useProductObservation(analytics, "validation_updated", {
        address_valid: shapeValid,
        can_submit: submitEnabled,
        status: verification.status
    });
    const showStatus = !!verification.text && shapeValid;
    const statusToneClass =
        verification.tone === "error"
            ? "text-error-base"
            : verification.tone === "success"
              ? "text-success-base"
              : verification.tone === "warning"
                ? "text-warning-base"
                : verification.tone === "muted"
                  ? "text-foreground-imprint"
                  : "text-foreground-neutral";
    return (
        <div
            className="pt-4 pb-1 flex flex-col gap-2"
            onBlur={() =>
                analytics.changed("changed", {
                    destination_kind:
                        trimmedAddress === connectedWallet ? "self" : "custom",
                    has_label: !!label.trim(),
                    address_valid: shapeValid
                })
            }
        >
            <LabeledRewardsDestination
                label={label}
                onLabelChange={setLabel}
                address={address}
                onAddressChange={setAddress}
                connectedWallet={connectedWallet}
                showDestinationLabel={false}
                addressPlaceholder="Account address"
            />
            <div className="flex items-start justify-between gap-3">
                {showStatus ? (
                    <Text
                        variant="fine-print"
                        render={<span />}
                        className={`${statusToneClass} min-w-0 truncate`}
                    >
                        {verification.text}
                    </Text>
                ) : (
                    <span />
                )}
                {/* Submit + Cancel share the trailing cluster; Cancel
                    collapses the accordion via the same `onClose` the
                    successful submit calls. */}
                <div className="flex items-center gap-3 shrink-0">
                    <Button
                        variant="sunken"
                        interaction="lifted"
                        size="sm"
                        className="shrink-0"
                        disabled={!submitEnabled}
                        onClick={() => {
                            if (!submitEnabled) return;
                            void (async (): Promise<void> => {
                                const result =
                                    await rewardsDestination.setRewardsDestination(
                                        {
                                            walletAddress,
                                            analytics,
                                            rewardsDestinationAddress:
                                                trimmedAddress
                                        }
                                    );
                                if (!result.success) {
                                    toastManager.add({
                                        title: "Rewards destination was not updated",
                                        description: result.error,
                                        data: { variant: "error" }
                                    });
                                    return;
                                }
                                toastManager.add({
                                    title: "Rewards destination updated",
                                    data: { variant: "success" }
                                });
                                analytics.close("completed");
                                onClose();
                            })();
                        }}
                    >
                        {rewardsDestination.isLoading
                            ? "Updating..."
                            : "Verify & Update"}
                    </Button>
                    <Button
                        variant="link"
                        size="sm"
                        disabled={rewardsDestination.isLoading}
                        onClick={() => {
                            analytics.close("cancel");
                            onClose();
                        }}
                    >
                        Cancel
                    </Button>
                </div>
            </div>
        </div>
    );
}
